class ImageSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.image-slider__wrapper');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.slides = this.querySelectorAll('.image-slider__slide');
    
    this.isDragging = false;
    this.startX = 0;
    this.scrollStart = 0;
    this.startTime = 0;
    this.lastX = 0;
    this.velocity = 0;
    
    if (!this.slider || !this.slides.length) return;
    
    this.init();
  }

  init() {
    const isSliderEnabled = this.slider.classList.contains('slider');
    
    if (!isSliderEnabled) {
      return;
    }

    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', this.onPrevClick.bind(this));
      this.nextButton.addEventListener('click', this.onNextClick.bind(this));
    }

    this.slider.addEventListener('scroll', this.updateButtonStates.bind(this));
    
    const resizeObserver = new ResizeObserver(() => this.updateButtonStates());
    resizeObserver.observe(this.slider);

    this.updateButtonStates();

    this.slider.addEventListener('keydown', this.onKeyDown.bind(this));

    this.setupDragFunctionality();
    
    this.setupClickHandling();
  }

  setupClickHandling() {

    this.slider.addEventListener('click', (e) => {
      if (this.dragDistance > 5) {

        if (e.target.closest('a, button')) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
    }, true);
  }

  setupDragFunctionality() {

    this.slider.addEventListener('mousedown', this.onDragStart.bind(this));
    this.slider.addEventListener('mousemove', this.onDragMove.bind(this));
    this.slider.addEventListener('mouseup', this.onDragEnd.bind(this));
    this.slider.addEventListener('mouseleave', this.onDragEnd.bind(this));


    this.slider.addEventListener('dragstart', (e) => e.preventDefault());
    

    this.slider.style.cursor = 'grab';
  }

  onDragStart(e) {

    if (e.type !== 'mousedown') return;
    
    this.isDragging = true;
    this.startTime = Date.now();
    this.dragDistance = 0;
    this.hasStartedDrag = false;
    

    this.startX = e.clientX;
    this.lastX = this.startX;
    this.scrollStart = this.slider.scrollLeft;
    this.velocity = 0;
    

    this.slider.style.cursor = 'grabbing';
    

    document.body.style.userSelect = 'none';
    

    e.preventDefault();
  }

  onDragMove(e) {
    if (!this.isDragging || e.type !== 'mousemove') return;
    

    const currentX = e.clientX;
    

    const deltaX = currentX - this.startX;
    this.dragDistance = Math.abs(deltaX);
    

    if (this.dragDistance > 5 && !this.hasStartedDrag) {
      this.hasStartedDrag = true;
      this.slider.style.scrollBehavior = 'auto';
      this.slider.classList.add('dragging');
    }
    

    if (this.hasStartedDrag) {
      const newScrollLeft = this.scrollStart - deltaX;
      

      const currentTime = Date.now();
      const deltaTime = currentTime - this.startTime;
      this.velocity = (this.lastX - currentX) / Math.max(deltaTime, 1);
      this.lastX = currentX;
      this.startTime = currentTime;
      

      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const boundedScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));
      
      this.slider.scrollLeft = boundedScrollLeft;
      

      this.updateButtonStates();
    }
  }

  onDragEnd() {
    if (!this.isDragging) return;
    
    const wasDragged = this.hasStartedDrag;
    this.isDragging = false;
    this.hasStartedDrag = false;
    

    this.slider.style.cursor = 'grab';
    this.slider.style.scrollBehavior = 'smooth';
    this.slider.classList.remove('dragging');
    document.body.style.userSelect = '';
    

    if (wasDragged) {

      const minVelocity = 0.5;
      if (Math.abs(this.velocity) > minVelocity) {
        const momentumDistance = this.velocity * 200;
        const currentScroll = this.slider.scrollLeft;
        const targetScroll = currentScroll + momentumDistance;
        

        const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
        const boundedTargetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
        

        this.slider.scrollTo({
          left: boundedTargetScroll,
          behavior: 'smooth'
        });
      } else {

        this.snapToNearestSlide();
      }
    }
    

    setTimeout(() => {
      this.dragDistance = 0;
    }, 10);
  }

  snapToNearestSlide() {
    const slideWidth = this.slides[0].offsetWidth;
    const gap = this.getGap();
    const slideWithGap = slideWidth + gap;
    const currentScroll = this.slider.scrollLeft;
    

    const nearestSlideIndex = Math.round(currentScroll / slideWithGap);
    const targetScroll = nearestSlideIndex * slideWithGap;
    

    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const boundedTargetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
    
    this.slider.scrollTo({
      left: boundedTargetScroll,
      behavior: 'smooth'
    });
  }

  onPrevClick() {
    const slideWidth = this.slides[0].offsetWidth;
    const scrollAmount = slideWidth + this.getGap();
    
    this.slider.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  }

  onNextClick() {
    const slideWidth = this.slides[0].offsetWidth;
    const scrollAmount = slideWidth + this.getGap();
    
    this.slider.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }

  getGap() {

    if (window.innerWidth <= 749) {
      return 20;
    }
    return 30;
  }

  updateButtonStates() {
    if (!this.prevButton || !this.nextButton) return;

    const scrollLeft = this.slider.scrollLeft;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;


    const isAtStart = scrollLeft <= 0;
    this.prevButton.disabled = isAtStart;
    this.prevButton.classList.toggle('disabled', isAtStart);
    this.prevButton.setAttribute('aria-disabled', isAtStart);


    const isAtEnd = scrollLeft >= maxScroll - 1; // -1 for rounding errors
    this.nextButton.disabled = isAtEnd;
    this.nextButton.classList.toggle('disabled', isAtEnd);
    this.nextButton.setAttribute('aria-disabled', isAtEnd);
  }

  onKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.onPrevClick();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.onNextClick();
    }
  }


  scrollToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;
    
    const slide = this.slides[index];
    const slideLeft = slide.offsetLeft;
    
    this.slider.scrollTo({
      left: slideLeft,
      behavior: 'smooth'
    });
  }
}

customElements.define('image-slider-component', ImageSliderComponent);
